// Copyright (c) Ping. All rights reserved.
//
// Host-side preview mirror for BACKEND-scheduled messages.
//
// The server stores only the opaque MLS ciphertext of a scheduled message and
// returns metadata (`id`, `send_at`, `created_at`) — never plaintext. So the
// bar above the composer can't get its preview text from the server. This
// module keeps a tiny local `${conversationId}:${sendAtMs} → { text, replyTo }`
// mirror (localStorage on web; an in-memory fallback elsewhere), written when
// the host schedules a message and read back when it lists pending ones.
//
// Keyed by conversation + exact send instant (not the server id, which the
// client doesn't learn until the subsequent list) — a collision needs two
// messages in the same conversation scheduled to the same millisecond, which
// the UI's second/minute-granularity pickers can't produce. Items scheduled on
// a previous device/session aren't in the mirror; those fall back to a generic
// label so they still appear (and stay cancelable).

import type { ScheduledMessageMeta } from "../../transport-mls";
import type { ScheduledMessage } from "./scheduledMessages";

const STORAGE_KEY = "ping.composer.scheduled.mirror";
/** Drop mirror rows older than this so abandoned entries can't grow unbounded. */
const MIRROR_MAX_AGE_MS = 31 * 24 * 60 * 60 * 1000; // 31 days (> 30-day horizon)
/** Shown when a pending item has no local preview (scheduled elsewhere). */
export const SCHEDULED_FALLBACK_TEXT = "Scheduled message";

export interface ScheduledMirrorEntry {
  readonly text: string;
  readonly replyTo?: string;
  /**
   * Hex content hash of the pre-encrypted message (from the `SentMessageEnvelope`
   * `conv.send()` returned at schedule time). It equals the hash the RECIPIENT
   * computes when the delivered message decrypts — so the sender's delivery
   * self-echo must carry it, or read/delivery receipts (keyed by content hash)
   * never match the echoed bubble. Absent on rows written before this landed.
   */
  readonly contentHash?: string;
  /** Epoch ms this entry was written (for age-pruning). */
  readonly savedAtMs: number;
}

type MirrorMap = Record<string, ScheduledMirrorEntry>;

/** Composite key: conversation + exact send instant. */
export function mirrorKey(conversationId: string, sendAtMs: number): string {
  return `${conversationId}:${sendAtMs}`;
}

function safeLocalStorage(): Storage | null {
  try {
    if (typeof localStorage === "undefined") return null;
    return localStorage;
  } catch {
    return null;
  }
}

// In-memory fallback so hosts without localStorage (react-native-macos) still
// get within-session previews.
let memoryMirror: MirrorMap = {};

function readMirror(): MirrorMap {
  const ls = safeLocalStorage();
  if (ls === null) return { ...memoryMirror };
  try {
    const raw = ls.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed = JSON.parse(raw) as MirrorMap;
    return parsed !== null && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeMirror(map: MirrorMap): void {
  // Age-prune on every write.
  const now = Date.now();
  const pruned: MirrorMap = {};
  for (const [k, v] of Object.entries(map)) {
    if (now - v.savedAtMs <= MIRROR_MAX_AGE_MS) pruned[k] = v;
  }
  const ls = safeLocalStorage();
  if (ls === null) {
    memoryMirror = pruned;
    return;
  }
  try {
    ls.setItem(STORAGE_KEY, JSON.stringify(pruned));
  } catch {
    // Quota / disabled — fall back to memory so the current session still works.
    memoryMirror = pruned;
  }
}

/**
 * Remember a scheduled message's preview text + its content hash (call right
 * after scheduling, with the hash from the `SentMessageEnvelope`).
 */
export function rememberScheduled(
  conversationId: string,
  sendAtMs: number,
  text: string,
  replyTo?: string,
  contentHash?: string,
): void {
  const map = readMirror();
  map[mirrorKey(conversationId, sendAtMs)] = {
    text,
    ...(replyTo !== undefined ? { replyTo } : {}),
    ...(contentHash !== undefined ? { contentHash } : {}),
    savedAtMs: Date.now(),
  };
  writeMirror(map);
}

/**
 * Map server metadata → the composer's `ScheduledMessage[]`, filling preview
 * text from the local mirror (or a fallback label). Pure given `mirror`; the
 * default reads the persistent store. Sorted soonest-first.
 */
export function mergeScheduledMeta(
  meta: readonly ScheduledMessageMeta[],
  conversationId: string,
  mirror: MirrorMap = readMirror(),
): ScheduledMessage[] {
  const out = meta.map((m) => {
    const entry = mirror[mirrorKey(conversationId, m.sendAtMs)];
    const text = entry?.text ?? SCHEDULED_FALLBACK_TEXT;
    const base: ScheduledMessage = {
      id: m.id,
      conversationId,
      text,
      sendAtMs: m.sendAtMs,
      createdAtMs: m.createdAtMs,
      ...(entry?.replyTo !== undefined ? { replyTo: entry.replyTo } : {}),
    };
    return base;
  });
  out.sort((a, b) => a.sendAtMs - b.sendAtMs);
  return out;
}

/** Read the current mirror (mainly for tests). */
export function readScheduledMirror(): MirrorMap {
  return readMirror();
}

/**
 * All mirror rows for one conversation (with their exact send instant). Used by
 * the host to reconcile deliveries: a row whose `sendAtMs` has passed and is no
 * longer in the server's pending list was delivered, so the host self-echoes it
 * into the timeline (the sender can't decrypt their own MLS message off the
 * wire) and then `forgetScheduled`s the row.
 */
export function listMirrorForConversation(
  conversationId: string,
): Array<{ sendAtMs: number; text: string; replyTo?: string; contentHash?: string }> {
  const map = readMirror();
  const prefix = `${conversationId}:`;
  const out: Array<{
    sendAtMs: number;
    text: string;
    replyTo?: string;
    contentHash?: string;
  }> = [];
  for (const [k, v] of Object.entries(map)) {
    if (!k.startsWith(prefix)) continue;
    const sendAtMs = Number(k.slice(prefix.length));
    if (Number.isNaN(sendAtMs)) continue;
    out.push({
      sendAtMs,
      text: v.text,
      ...(v.replyTo !== undefined ? { replyTo: v.replyTo } : {}),
      ...(v.contentHash !== undefined ? { contentHash: v.contentHash } : {}),
    });
  }
  return out;
}

/** Drop a mirror row (on delivery-echo or user cancel). */
export function forgetScheduled(conversationId: string, sendAtMs: number): void {
  const map = readMirror();
  const key = mirrorKey(conversationId, sendAtMs);
  if (map[key] !== undefined) {
    delete map[key];
    writeMirror(map);
  }
}
